import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button, Modal, Header, Spacer, TextField, Checkbox, Box, Text } from '@truepill/react-capsule'
import { PriorAuthorizationStatus } from '@truepill/tpos-types'
import { ReactComponent as LoadingAnimation } from 'assets/icons/loading_dots.svg'
import { ReactComponent as FaxPriorAuthIcon } from 'assets/icons/print.svg'
import ActionButton from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { FAX_PA_TO_PRESCRIBER } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import styled from 'styled-components'
import { bodyPrimaryColor, contrastBackgroundColor } from 'styles/styleVariables'
import type { Prescription, PriorAuthorization } from 'types'
import { formatAddress, getPrescriberAddress } from 'utils'

type FaxPriorAuthorizationButtonProps = {
  prescription: Prescription
  priorAuthorization?: PriorAuthorization
  disabled?: boolean
  onSubmit?: () => void
  enabled?: boolean
}

interface ModalProps {
  prescription: Prescription
  priorAuthorization: PriorAuthorization
  isOpen: boolean
  onDismiss: () => void
  onSubmit?: () => void
}

export const FaxPAModal = ({ priorAuthorization, prescription, isOpen, onDismiss, onSubmit }: ModalProps) => {
  const [useDiffFaxNumber, setUseDiffFaxNumber] = useState(false)
  const showErrorToast = useErrorToast()
  const showSuccessToast = useSuccessToast()
  const [faxNumber, setFaxNumber] = useState('')
  const [sendFax, { loading: sendingFax }] = useMutation(FAX_PA_TO_PRESCRIBER, {
    onCompleted: _data => {
      showSuccessToast('Fax sent successfully')
      onDismiss()
    },
    onError: error => {
      console.error('Fax attempt failed. Please try again.' + error)
      showErrorToast('Fax attempt failed. Please try again.')
      onDismiss()
    },
  })

  const handleFaxNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setFaxNumber(value)
  }

  const prescriber = prescription.prescriber
  const prescriberAddress = getPrescriberAddress(prescription)
  const isInvalid = useDiffFaxNumber ? !faxNumber : !prescriber?.contacts?.fax && !prescriberAddress?.fax

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} aria-labelledby="title" overlayCss={{ 'z-index': 10 }}>
      <>
        <StyledHeader variant="xl" id="title">
          Fax PA
        </StyledHeader>
        <Spacer size="xl" />
        {!prescriber && <Box>Could not load prescriber information</Box>}
        {prescriber && (
          <>
            <Text css={{ fontWeight: 'bold' }}>Prescriber</Text>
            <Spacer size="xs" />
            <PrescriberBox>
              <Text css={{ fontWeight: 'bold' }}>
                {prescriber.firstName} {prescriber.lastName}
              </Text>
              <Spacer size="xs" />
              <Box>{formatAddress(prescriberAddress)}</Box>
              <Box css={{ display: 'flex', gap: '.5rem', flexFlow: 'row' }}>
                <Text css={{ fontWeight: 'bold' }}>Phone:</Text> {prescriber.contacts?.phone ?? '-'}
              </Box>
              <Box css={{ display: 'flex', gap: '.5rem', flexFlow: 'row' }}>
                <Text css={{ fontWeight: 'bold' }}>Fax:</Text>{' '}
                {prescriberAddress?.fax ?? prescriber.contacts?.fax ?? '-'}
              </Box>
            </PrescriberBox>
          </>
        )}
        <Spacer size="sm" />
        <Box>
          <Checkbox
            size="md"
            label="Use a different fax number"
            checked={useDiffFaxNumber}
            onCheckedChange={checked => {
              setUseDiffFaxNumber(!!checked)
            }}
          />
        </Box>
        <Spacer size="xs" />
        {useDiffFaxNumber && <TextField onChange={handleFaxNumberChange} size="sm" label="Alternate fax number" />}
        <Spacer size="xl" />
        <StyledContainer>
          <Button variant="primary-text" size="md" onClick={onDismiss}>
            Cancel
          </Button>
          <Button
            size="md"
            disabled={isInvalid || sendingFax}
            onClick={() => {
              sendFax({
                variables: {
                  priorAuthorizationId: priorAuthorization._id,
                  faxNumber: useDiffFaxNumber ? faxNumber : prescriberAddress?.fax ?? prescriber.contacts?.fax,
                },
              })
              onSubmit?.()
            }}
          >
            {sendingFax ? <LoadingAnimation role="img" /> : 'Send fax'}
          </Button>
        </StyledContainer>
      </>
    </Modal>
  )
}

const invalidStatuses = [
  PriorAuthorizationStatus.Inactive,
  PriorAuthorizationStatus.Cancelled,
  PriorAuthorizationStatus.Approved,
  PriorAuthorizationStatus.Denied,
]

export const FaxPriorAuthorizationButton = ({
  priorAuthorization,
  disabled = false,
  prescription,
  onSubmit,
  enabled = true,
}: FaxPriorAuthorizationButtonProps): JSX.Element => {
  const [openModal, setOpenModal] = useState(false)

  const handleOnClick = () => {
    setOpenModal(true)
  }

  if (!enabled) {
    console.warn('Fax PA Button is disabled (LD)')
    return <></>
  }

  if (!priorAuthorization) {
    return <></>
  }

  return (
    <>
      <ActionButton
        disabled={disabled || invalidStatuses.includes(priorAuthorization.status)}
        icon={
          <IconWrapper>
            <FaxPriorAuthIcon stroke={bodyPrimaryColor} />
          </IconWrapper>
        }
        label={'Fax PA'}
        onClick={handleOnClick}
      />
      <FaxPAModal
        isOpen={openModal}
        onDismiss={() => setOpenModal(false)}
        priorAuthorization={priorAuthorization}
        prescription={prescription}
        onSubmit={onSubmit}
      />
    </>
  )
}

const PrescriberBox = styled(Box)`
  background-color: ${contrastBackgroundColor};
  padding: 8px;
  border-radius: 0.5rem;
`
const StyledHeader = styled(Header)`
  font-weight: bold;
  font-size: 36px;
`

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  gap: 15px;
`
